import { useState } from 'react';
import { RelayTile } from './RelayTile';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus; gateDisabled: boolean }

export function RelayGrid({ device, gateDisabled }: Props) {
  const total = device.totalRelays ?? device.relays?.length ?? 0;
  const relays = device.relays ?? [];
  const [pending, setPending] = useState<Set<number>>(new Set());

  const mutation = useDeviceMutation<{ id: number; state: 0 | 1 }>(
    device.client_id, 'relay',
    {
      optimistic: (prev, vars) => {
        if (!prev || !prev.relays) return prev;
        const next = { ...prev, relays: prev.relays.map((r, i) =>
          i === vars.id ? { ...r, state: vars.state } : r) };
        return next;
      },
      successToast: (v) => `Relay ${v.id + 1} → ${v.state ? 'ON' : 'OFF'}`,
    },
  );

  const onToggle = async (id: number, next: 0 | 1) => {
    setPending((s) => new Set(s).add(id));
    try {
      await mutation.mutateAsync({ id, state: next });
    } finally {
      setTimeout(() => {
        setPending((s) => { const n = new Set(s); n.delete(id); return n; });
      }, 1200);
    }
  };

  if (total === 0) {
    return <div className="text-sm text-slate-500 text-center py-8">No relays reported yet.</div>;
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2.5">
      {Array.from({ length: total }).map((_, i) => (
        <RelayTile
          key={i}
          device={device}
          index={i}
          relay={relays[i] ?? {}}
          pending={pending.has(i)}
          disabled={gateDisabled}
          onToggle={(next) => onToggle(i, next)}
        />
      ))}
    </div>
  );
}
